import React, { useState, useRef, useEffect } from 'react'
import { Box, Typography, TextField, Button, Paper, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'
import toaster from '../../toaster'
import Spinner from '../../components/common/alertFetchSpinner/processingSpinner'
import './BackOffice.scss'
import { useGuenoFetch } from '../../services/guenoApi'

const inputButtonHeight = 56 // px, matches .reset-button in SCSS

const BackOffice = () => {
  const { t } = useTranslation(['common'])
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { guenoFetch } = useGuenoFetch()
  
  // Rule for historical transactions state
  const [ruleEmail, setRuleEmail] = useState('')
  const [ruleMaskId, setRuleMaskId] = useState('')
  const [isRunningRule, setIsRunningRule] = useState(false)
  const [currentPid, setCurrentPid] = useState('')
  const [isCheckingProgress, setIsCheckingProgress] = useState(false)
  const [progressLog, setProgressLog] = useState('')
  const [emailError, setEmailError] = useState('')
  const [ruleMaskIdError, setRuleMaskIdError] = useState('')
  const progressIntervalRef = useRef(null)

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateRuleMaskId = (ruleMaskId) => {
    return ruleMaskId.trim() !== ''
  }

  // Handle email change with validation
  const handleRuleEmailChange = (value) => {
    setRuleEmail(value)
    if (value === '') {
      setEmailError('Email is required')
    } else if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  // Handle rule mask ID change with validation
  const handleRuleMaskIdChange = (value) => {
    setRuleMaskId(value)
    if (!validateRuleMaskId(value)) {
      setRuleMaskIdError('Rule MaskId is required')
    } else {
      setRuleMaskIdError('')
    }
  }

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  const checkProgress = async (pid) => {
    try {
      const response = await guenoFetch(`${process.env.REACT_APP_BASEURL}/kyt-back-office/runRuleForHistoricalTransactionsVerifyProgress/pid/${encodeURIComponent(pid)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.text()

      if (response.ok) {
        // Log the progress response
        setProgressLog(data || 'No response')
        
        // Check if the response contains "Error"
        if (data && data.toLowerCase().includes('error')) {
          // Task failed with error
          clearInterval(progressIntervalRef.current)
          progressIntervalRef.current = null
          setIsCheckingProgress(false)
          setCurrentPid('')
          setProgressLog('')
          toaster.error(data || 'Task failed with error')
          return
        }
        
        // Check if the response contains "done"
        if (data && data.toLowerCase().includes('done')) {
          // Task completed successfully
          clearInterval(progressIntervalRef.current)
          progressIntervalRef.current = null
          setIsCheckingProgress(false)
          setCurrentPid('')
          setProgressLog('')
          toaster.success(t('common:taskCompleted'))
          
          // Reset form
          setRuleEmail('')
          setRuleMaskId('')
        }
        // If not done and no error, continue polling (interval will handle next check)
      } else {
        console.error('Error checking progress:', data)
        setProgressLog(`HTTP Error: ${data || 'Unknown error'}`)
        // Continue polling even on HTTP error, unless it's a critical error
      }
    } catch (error) {
      console.error('Error checking progress:', error)
      setProgressLog(`Network error: ${error.message}`)
      // Continue polling on network errors
    }
  }

  const startProgressPolling = (pid) => {
    setCurrentPid(pid)
    setIsCheckingProgress(true)
    
    // Start polling every 3 seconds
    progressIntervalRef.current = setInterval(() => {
      checkProgress(pid)
    }, 3000)
    
    // Check immediately
    checkProgress(pid)
  }

  const stopProgressPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
    setIsCheckingProgress(false)
    setCurrentPid('')
    setProgressLog('')
  }

  const handleRunRule = async () => {
    // Validate email
    if (!ruleEmail) {
      setEmailError('Email is required')
      toaster.error('Please enter an email address')
      return
    }

    if (!validateEmail(ruleEmail)) {
      setEmailError('Please enter a valid email address')
      toaster.error('Please enter a valid email address')
      return
    }

    // Validate rule mask ID
    if (!validateRuleMaskId(ruleMaskId)) {
      setRuleMaskIdError('Rule MaskId is required')
      toaster.error('Please enter a rule mask ID')
      return
    }

    // Clear any previous errors
    setEmailError('')
    setRuleMaskIdError('')

    // Stop any existing progress polling
    stopProgressPolling()
    setIsRunningRule(true)

    try {
      const response = await guenoFetch(`${process.env.REACT_APP_BASEURL}/kyt-back-office/runRuleForHistoricalTransactions/emailClient/${encodeURIComponent(ruleEmail)}/ruleMaskId/${encodeURIComponent(ruleMaskId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.text()
      if (response.ok) {
        // Check if response contains a PID (string)
        if (data && data.trim() !== '') {
          // Got PID, start progress polling
          toaster.success('Task started successfully. Checking progress...')
          setIsRunningRule(false) // Stop the initial loading state
          startProgressPolling(data.trim())
        } else {
          // No PID received, treat as immediate completion
          toaster.success('Rule executed successfully')
          setRuleEmail('')
          setRuleMaskId('')
          setIsRunningRule(false)
        }
      } else {
        let messageToShow = data || 'Failed to run rule'
        toaster.error(messageToShow)
        setIsRunningRule(false)
      }
    } catch (error) {
      console.error('Error running rule:', error)
      toaster.error('An error occurred while running the rule')
      setIsRunningRule(false)
    }
  }

  const handleResetMFA = async () => {
    if (!email) {
      toaster.error('Please enter an email address')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toaster.error('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      const response = await guenoFetch(`${process.env.REACT_APP_BASEURL}/user/backOffice/resetMfa?email=${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (response.ok) {
        toaster.success(data.message || 'MFA reset successful')
        setEmail('')
      } else {
          let messageToShow = data.message || 'Failed to reset MFA'
          if (data.data) {
             messageToShow+= `(${data.data})` 
          }
        toaster.error(messageToShow)
      }
    } catch (error) {
      console.error('Error resetting MFA:', error)
      toaster.error('An error occurred while resetting MFA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleResetMFA()
    }
  }

  return (
    <Box className="backoffice-container">
      <Typography variant="h2" className="page-title">
        BackOffice
      </Typography>

      <Paper elevation={0} className="backoffice-content">
        <Typography variant="h3" className="section-title">
          Reset MFA
        </Typography>
        <Divider className="section-divider" />

        <Box className="reset-mfa-section">
          <Typography variant="body1" className="section-description">
            Enter an email address to reset the MFA (Multi-Factor Authentication) for that user.
          </Typography>

          <Box className="input-section">
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              fullWidth
              variant="outlined"
              placeholder="Enter email address"
              className="email-input"
              InputProps={{
                style: { height: inputButtonHeight, boxSizing: 'border-box', padding: 0 },
              }}
              InputLabelProps={{
                style: { lineHeight: `${inputButtonHeight}px` },
              }}
            />

            <Button
              variant="contained"
              onClick={handleResetMFA}
              disabled={isLoading || !email}
              className="reset-button"
              startIcon={isLoading ? <Spinner size={20} /> : null}
              style={{ height: inputButtonHeight, minWidth: 140 }}
            >
              {isLoading ? 'Resetting MFA...' : 'Reset MFA'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Run Rule for Historical Transactions Section */}
      <Paper elevation={0} className="backoffice-content" style={{ marginTop: '2rem' }}>
        <Typography variant="h3" className="section-title">
          {t('common:runRuleForHistoricalTransactions')}
        </Typography>
        <Divider className="section-divider" />

        <Box className="run-rule-section">
          <Typography variant="body1" className="section-description">
            Configure parameters for running rules on historical transactions.
          </Typography>

          {isCheckingProgress && currentPid && (
            <Box className="progress-info">
              <Typography variant="body2" color="primary">
                {t('common:taskInProgress')} (PID: {currentPid})
              </Typography>
              {progressLog && (
                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  <strong>Progress:</strong> {progressLog}
                </Typography>
              )}
            </Box>
          )}

          <Box className="rule-inputs">
            <Box className="rule-inputs-row">
              <TextField
                label={t('common:email')}
                type="email"
                value={ruleEmail}
                onChange={(e) => handleRuleEmailChange(e.target.value)}
                variant="outlined"
                placeholder={t('common:enterEmail')}
                className="rule-email-input"
                disabled={isRunningRule || isCheckingProgress}
                error={!!emailError}
                helperText={emailError}
                required
                InputProps={{
                  style: { height: inputButtonHeight, boxSizing: 'border-box', padding: 0 },
                }}
                InputLabelProps={{
                  style: { lineHeight: `${inputButtonHeight}px` },
                }}
              />

              <TextField
                label={t('common:ruleMaskId')}
                value={ruleMaskId}
                onChange={(e) => handleRuleMaskIdChange(e.target.value)}
                variant="outlined"
                placeholder={t('common:enterRuleMaskId')}
                className="rule-mask-input"
                disabled={isRunningRule || isCheckingProgress}
                error={!!ruleMaskIdError}
                helperText={ruleMaskIdError}
                required
                InputProps={{
                  style: { height: inputButtonHeight, boxSizing: 'border-box', padding: 0 },
                }}
                InputLabelProps={{
                  style: { lineHeight: `${inputButtonHeight}px` },
                }}
              />

              <Button
                variant="contained"
                onClick={handleRunRule}
                disabled={(isRunningRule || isCheckingProgress) || !ruleEmail || !ruleMaskId || !!emailError || !!ruleMaskIdError}
                className="run-rule-button"
                startIcon={(isRunningRule || isCheckingProgress) ? <Spinner size={20} /> : null}
                style={{ height: inputButtonHeight, minWidth: 120 }}
              >
                {isRunningRule ? t('common:running') : isCheckingProgress ? t('common:checkingProgress') : t('common:run')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default BackOffice
